import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { Input } from '../../../../components/forms';
import Select from '../../../../components/forms/Select';
import Button from '../../../../components/common/Button';
import Alert from '../../../../components/common/Alert';
import { FormControlLabel } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import InputWrapper from '../../../../components/forms/Input/inputWrapper';
import Checkbox from '@material-ui/core/Checkbox';
import ControlPointSharpIcon from '@material-ui/icons/ControlPointSharp';
import InstitutionInput from '../../../../components/forms/Input/institutionInput';
import AddCircleOutlineSharpIcon from '@material-ui/icons/AddCircleOutlineSharp';
import Dropdown from '../../../WorkShopContainer/components/PillarDropDown';
import InputImage from '../../../../components/forms/Input/inputImage';

const COURSE_LEVEL_OPTIONS = ['Principiante', 'Intermedio', 'Avanzado'];
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const createCourseSlug = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

const CourseSlugSync = () => {
  const { values, setFieldValue } = useFormikContext();
  const previousNameRef = useRef(values.name || '');

  useEffect(() => {
    const previousGeneratedSlug = createCourseSlug(previousNameRef.current);
    const currentSlug = values.slug || '';
    const shouldSyncSlug = !currentSlug || currentSlug === previousGeneratedSlug;

    if (shouldSyncSlug) {
      const nextSlug = createCourseSlug(values.name);
      if (nextSlug !== currentSlug) {
        setFieldValue('slug', nextSlug, false);
      }
    }

    previousNameRef.current = values.name || '';
  }, [setFieldValue, values.name, values.slug]);

  return null;
};

const normalizeInstitutionForPayload = (institution, institutionsList = []) => {
  if (!institution) {
    return institution;
  }

  if (typeof institution === 'object') {
    const normalizedName = institution.name || institution.value || institution.label || '';
    let resolvedId = institution.id;

    if (
      (resolvedId === undefined ||
        resolvedId === null ||
        resolvedId === '') &&
      Array.isArray(institutionsList) &&
      institutionsList.length > 0
    ) {
      const normalizedNameLower = normalizedName
        ? normalizedName.toLowerCase().trim()
        : '';

      const fallbackInstitution = institutionsList.find((inst) => {
        const instName = inst.name ? inst.name.toLowerCase().trim() : '';
        const instValue = inst.value ? inst.value.toLowerCase().trim() : '';
        const instLabel = inst.label ? inst.label.toLowerCase().trim() : '';

        return (
          (instName && instName === normalizedNameLower) ||
          (instValue && instValue === normalizedNameLower) ||
          (instLabel && instLabel === normalizedNameLower)
        );
      });

      if (fallbackInstitution) {
        resolvedId = fallbackInstitution.id;
      }
    }

    const parsedId =
      resolvedId !== undefined &&
      resolvedId !== null &&
      resolvedId !== '' &&
      !Number.isNaN(Number(resolvedId))
        ? Number(resolvedId)
        : resolvedId;

    return {
      ...institution,
      id: parsedId,
      name: normalizedName,
      value: institution.value || normalizedName,
      logo: institution.logo,
    };
  }

  const match = institutionsList.find(
    (inst) => Number(inst.id) === Number(institution)
  );

  if (match) {
    const normalizedName = match.name || match.value || match.label || '';

    return {
      ...match,
      id:
        match.id !== undefined && match.id !== null ? Number(match.id) : match.id,
      name: normalizedName,
      value: match.value || normalizedName,
      logo: match.logo,
    };
  }

  return institution;
};

const CourseCreationContainer = ({
  onNext,
  course,
  setCourse,
  pillars,
  institutions = [],
  setInstitutions,
  setRefresh,
  handleFormSubmit,
  mode = 'create',
}) => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [messageAlert, setMessageAlert] = useState('');
  const [selectedPillar, setSelectedPillar] = useState();
  const [showInstitutionInput, setShowInstitutionInput] = useState(false);
  const [previousInstitutionValue, setPreviousInstitutionValue] = useState(null);

  const submitButtonLabel = mode === 'edit' ? 'Actualizar' : 'Aceptar';

  const onClickPillar = (option, setFieldValue) => {
    setFieldValue('pillar', option.id);
    setSelectedPillar(option);
  };

  const initialValues = useMemo(() => {
    const institutionValue =
      typeof course?.institution === 'object'
        ? course.institution?.id !== undefined && course.institution?.id !== null
          ? String(course.institution.id)
          : ''
        : course?.institution !== undefined && course?.institution !== null
        ? String(course?.institution)
        : '';

    return {
      ...course,
      pillar:
        typeof course?.pillar === 'object'
          ? course.pillar.id
          : course?.pillar || '',
      institution: institutionValue,
      nivel: course?.nivel || 'Principiante',
      slug: course?.slug || createCourseSlug(course?.name),
      image: course?.image || '',
    };
  }, [course]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required('Nombre requerido'),
        slug: Yup.string()
          .required('Slug requerido')
          .matches(
            SLUG_PATTERN,
            'Usa solo minúsculas, números y guiones, sin espacios'
          ),
        institution: Yup.mixed().required('Institución requerida'),
        estimatedTime: Yup.string().required('Tiempo requerido'),
        nivel: Yup.string()
          .oneOf(COURSE_LEVEL_OPTIONS, 'Nivel inválido')
          .required('Nivel requerido'),
        hash: Yup.string().required('Hash requerido'),
        pillar: Yup.number().required('Pilar requerido').min(1),
        active: Yup.boolean(),
        hasVideo: Yup.boolean(),
        hasReading: Yup.boolean(),
        hasDownload: Yup.boolean(),
        image: Yup.mixed().required('Imágen requerida'),
        description: Yup.string().required('Descripción requerida'),
      }),
    []
  );

  const buildPayload = (values) => {
    const institutionValue =
      typeof values.institution === 'object' && values.institution !== null
        ? values.institution
        : institutions?.find(
            (institution) => Number(institution.id) === Number(values.institution)
          ) || values.institution || '';

    const normalizedInstitution = normalizeInstitutionForPayload(
      institutionValue,
      institutions
    );

    const payload = {
      ...values,
      slug: createCourseSlug(values.slug || values.name),
      pillar: values.pillar ? Number(values.pillar) : values.pillar,
      institution: normalizedInstitution,
    };

    const hasNewImage = Boolean(
      values.image && (typeof values.image === 'string' || values.image.data)
    );
    const fallbackImage = course?.image;

    if (!hasNewImage && fallbackImage) {
      payload.image = fallbackImage;
    }

    return payload;
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);

    const payload = buildPayload(values);

    try {
      if (handleFormSubmit) {
        await Promise.resolve(handleFormSubmit(payload));
      } else {
        if (setCourse) {
          setCourse(payload);
        }
        if (onNext) {
          onNext();
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const Cancel = () => {
    history.push('/cursos');
  };

  const onInstitutionSubmit = (institution, setFieldValue) => {
    const institutionData = normalizeInstitutionForPayload(
      {
        ...institution,
        value: institution.name || institution.value || '',
      },
      institutions
    );

    const institutionForCourse = {
      ...institutionData,
      id:
        institutionData?.id !== undefined && institutionData?.id !== null
          ? String(institutionData.id)
          : institutionData?.id,
    };
    if (setCourse) {
      setCourse((prevCourse) => ({
        ...prevCourse,
        institution: institutionForCourse,
      }));
    }
    setPreviousInstitutionValue(null);
    setShowInstitutionInput(false);
    const institutionValueForField =
      typeof institutionForCourse === 'object' && institutionForCourse !== null
        ? institutionForCourse.id ??
          institutionForCourse.value ??
          institutionForCourse.name ??
          ''
        : institutionForCourse;
    setFieldValue('institution', institutionValueForField);
  };

  const handleNewInstitution = (newInstitution) => {
    if (typeof setInstitutions === 'function') {
      setInstitutions((prevInstitutions) => {
        const safeInstitutions = Array.isArray(prevInstitutions)
          ? prevInstitutions
          : [];

        const normalizedInstitution = normalizeInstitutionForPayload(
          newInstitution,
          safeInstitutions
        );

        const normalizedInstitutionForList = {
          ...normalizedInstitution,
          id:
            normalizedInstitution?.id !== undefined &&
            normalizedInstitution?.id !== null
              ? String(normalizedInstitution.id)
              : normalizedInstitution?.id,
        };

        const hasInstitution = safeInstitutions.some(
          (institutionItem) =>
            Number(institutionItem.id) === Number(normalizedInstitution.id)
        );

        if (hasInstitution) {
          return safeInstitutions.map((institutionItem) =>
            Number(institutionItem.id) === Number(normalizedInstitution.id)
              ? normalizedInstitutionForList
              : institutionItem
          );
        }

        return [...safeInstitutions, normalizedInstitutionForList];
      });
    }

    if (typeof setRefresh === 'function') {
      setRefresh((prev) => !prev);
    }
  };

  useEffect(() => {
    const pillarId =
      typeof course?.pillar === 'object' ? course.pillar.id : course?.pillar;

    if (!pillarId) {
      setSelectedPillar(undefined);
      return;
    }

    const foundPillar = pillars.find(
      (p) => Number(p.id) === Number(pillarId)
    );

    if (foundPillar) {
      setSelectedPillar(foundPillar);
    }
  }, [course?.pillar, pillars]);

  return (
    <div className="wizard__form wizard__form--full">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
      {({ values, errors, touched, setFieldValue, setFieldTouched }) => {
        const showSlugWarning =
          mode === 'edit' &&
          course?.active &&
          course?.slug &&
          values.slug &&
          values.slug !== course.slug;

        const handleOpenInstitutionInput = () => {
          setPreviousInstitutionValue(values.institution);
          setFieldValue('institution', { name: '', logo: null });
          setShowInstitutionInput(true);
        };

        const handleCancelInstitutionInput = () => {
          setShowInstitutionInput(false);

          if (
            previousInstitutionValue !== null &&
            previousInstitutionValue !== undefined
          ) {
            setFieldValue('institution', previousInstitutionValue);
          } else {
            setFieldValue('institution', '');
          }
        };

        return (
          <Form className="course-creation">
            <CourseSlugSync />
            {showAlert && <Alert type="error">{messageAlert}</Alert>}

              <section className="course-creation__card">
                <h2 className="course-creation__title">Información del curso</h2>

                <div className="course-creation__grid">
                  <div className="course-creation__field">
                    <span>
                      <strong>Nombre</strong>
                    </span>
                    <InputWrapper>
                      <Input name="name" placeholder="Nombre" label="" />
                    </InputWrapper>
                  </div>

                  <div className="course-creation__field">
                    <span>
                      <strong>Slug</strong>
                    </span>
                    <InputWrapper>
                      <Input
                        name="slug"
                        placeholder="slug-del-curso"
                        label=""
                        extra="URL pública del curso"
                      />
                    </InputWrapper>
                    {showSlugWarning ? (
                      <Alert type="warning">
                        Cambiar el slug modifica la URL pública. Las URLs antiguas con ID seguirán redirigiendo al slug actual.
                      </Alert>
                    ) : null}
                  </div>

                  <div className="course-creation__field course-creation__field--stacked">
                    <div className="course-creation__field-header">
                      <span>
                        <strong>Institución</strong>
                      </span>
                      {!showInstitutionInput && (
                        <button
                          type="button"
                          className="course-creation__helper-link"
                          onClick={handleOpenInstitutionInput}
                        >
                          <AddCircleOutlineSharpIcon /> Agregar nueva Institución
                        </button>
                      )}
                    </div>

                    {showInstitutionInput ? (
                      <>
                        <InstitutionInput
                          onClose={handleCancelInstitutionInput}
                          onInstitutionSubmit={onInstitutionSubmit}
                          onClickAccept={handleNewInstitution}
                        />
                        <button
                          type="button"
                          className="course-creation__helper-link"
                          onClick={handleCancelInstitutionInput}
                        >
                          <ControlPointSharpIcon /> Seleccionar Institución existente
                        </button>
                      </>
                    ) : (
                      <Select
                        name="institution"
                        label={"Selecciona Institución"}
                        options={[]}
                        advancedOptions={institutions}
                        isRequired={true}
                      />
                    )}
                  </div>

                  <div className="course-creation__field course-creation__field--stacked">
                    <span>
                      <strong>Descripción</strong>
                    </span>
                    <InputWrapper variant="textarea">
                      <Input
                        name="description"
                        type="textarea"
                        max={500}
                        showCounter
                      />
                    </InputWrapper>
                  </div>

                  <div className="course-creation__field course-creation__field--stacked">
                    <span>
                      <strong>Tipo de contenido</strong>
                    </span>
                    <div className="course-creation__checkbox-group">
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="hasVideo"
                            name="hasVideo"
                            style={{ color: '#38bdf8' }}
                            onChange={(e) =>
                              setFieldValue('hasVideo', e.target.checked)
                            }
                            color="primary"
                            checked={values.hasVideo}
                          />
                        }
                        label="Videos"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="hasReading"
                            id="hasReading"
                            style={{ color: '#38bdf8' }}
                            onChange={(e) =>
                              setFieldValue('hasReading', e.target.checked)
                            }
                            color="primary"
                            checked={values.hasReading}
                          />
                        }
                        label="Material de lectura"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="hasDownload"
                            id="hasDownload"
                            style={{ color: '#38bdf8' }}
                            onChange={(e) =>
                              setFieldValue('hasDownload', e.target.checked)
                            }
                            color="primary"
                            checked={values.hasDownload}
                          />
                        }
                        label="Material descargable"
                      />
                    </div>
                  </div>

                  <div className="course-creation__field">
                    <span>
                      <strong>Hash de Curso</strong>
                    </span>
                    <InputWrapper>
                      <Input name="hash" placeholder="Hash" label="" />
                    </InputWrapper>
                  </div>

                  <div className="course-creation__field">
                    <span>
                      <strong>Tiempo Estimado</strong>
                    </span>
                    <InputWrapper>
                      <Input
                        name="estimatedTime"
                        placeholder="Tiempo estimado"
                        label=""
                      />
                    </InputWrapper>
                  </div>

                  <div className="course-creation__field">
                    <span>
                      <strong>Nivel</strong>
                    </span>
                    <Select
                      name="nivel"
                      label="Selecciona nivel"
                      options={COURSE_LEVEL_OPTIONS}
                      advancedOptions={[]}
                      isRequired={true}
                    />
                  </div>

                  <div className="course-creation__field">
                    <span>
                      <strong>Pilar</strong>
                    </span>

                    <Dropdown
                      id="pillar"
                      label="Selecciona Pilar"
                      name="pillar"
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      options={pillars.map((pillar) => ({
                        name: pillar.name,
                        id: pillar.id,
                      }))}
                      selectedOption={selectedPillar}
                      onClick={(option) => onClickPillar(option, setFieldValue)}
                    />
                    {touched.pillar && errors.pillar ? (
                      <span className="error-message">{errors.pillar}</span>
                    ) : null}
                  </div>

                  <div className="course-creation__field course-creation__field--full">
                    <InputWrapper
                      variant="big"
                      label="Imágen"
                      description="Archivos 240x200px JPG, PNG  - Tamaño máximo 5MB."
                      id="image"
                    >
                      <InputImage
                        name="image"
                        id="image"
                        allowedExts={['image/png', 'image/jpg', 'image/jpeg']}
                      />
                    </InputWrapper>
                  </div>
                </div>
              </section>

              <div className="course-creation__actions">
                <div className="wizard__action">
                  <Button
                    variant="outlined"
                    color="default"
                    widthFull
                    secondary
                    onClick={Cancel}
                  >
                    Cancelar
                  </Button>

                  <Button type="submit" widthFull disabled={isLoading}>
                    {submitButtonLabel}
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CourseCreationContainer;
